import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getFlights, getAircraftSpecs, getApronCompatibility, getApronCompatibilityBatch, updateFlight, createFlight, getBatchFlightTracking, getLiveTracking, triggerTrackingPoll, lookupAirport, lookupAircraft, getFlightDates, getServices, checkStandCompatibility, getOperationalRestrictions } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plane, ArrowRight, Calendar as CalendarIcon, Filter, Loader2, Users, Clock, Fuel, User, Briefcase, ParkingCircle, Pencil, Search, RotateCcw, Ruler, ArrowUpDown, Scale, Check, X, FileText, Save, Plus, PlaneLanding, PlaneTakeoff, AlertTriangle, Radio, MapPin, ArrowUp, ArrowDown, Ban, Lock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { FlightHistoryButton } from "@/components/FlightHistoryButton";
import { MvtButton } from "@/components/MvtButton";
import { BriefingButton } from "@/components/BriefingButton";
import { FlightDocButton } from "@/components/FlightDocButton";
import { PprBadge } from "@/components/PprBadge";
import { DerogationBadge } from "@/components/DerogationBadge";
import { useAvailableYears } from "@/hooks/useAvailableYears";

// Icon mapping for dynamic services
const serviceIconMap = {
  "fuel": "⛽",
  "car": "🚕",
  "utensils": "🍽️",
  "droplet": "🚿",
  "glass-water": "💧",
  "plug": "⚡",
  "coffee": "☕",
  "snowflake": "🧊",
  "plate-wheat": "🍽️",
  "shirt": "🧺",
  "wind": "🔧",
  "shield-alert": "⚠️",
  "accessibility": "♿",
  "thermometer-snowflake": "❄️",
  "circle": "•",
  "receipt": "🧾",
  "badge-check": "✅",
  "warehouse": "🏠",
  "truck": "🚛",
};

// Color mapping for service badges 
const serviceColorMap = {
  "fuel_request": "bg-amber-500 text-white",
  "taxi_request": "bg-purple-500 text-white",
  "catering_request": "bg-pink-500 text-white",
  "gpu_request": "bg-yellow-500 text-black",
  "wc_service_request": "bg-cyan-500 text-white",
  "potable_water_request": "bg-blue-500 text-white",
  "coffee_request": "bg-amber-700 text-white",
  "ice_request": "bg-sky-400 text-white",
  "dishes_request": "bg-orange-500 text-white",
  "laundry_request": "bg-violet-500 text-white",
  "asu_request": "bg-emerald-500 text-white",
  "de_icing_request": "bg-indigo-500 text-white",
  "detax_request": "bg-lime-500 text-white",
  "customs_request": "bg-rose-500 text-white",
  "de_tax___customs_request": "bg-rose-500 text-white",
  "hangar_request": "bg-slate-500 text-white",
  "towing_request": "bg-stone-500 text-white",
  "weapon_on_board": "bg-red-600 text-white font-bold animate-pulse",
  "wchr_request": "bg-blue-600 text-white font-bold animate-pulse",
  "pmr_request": "bg-blue-600 text-white font-bold animate-pulse",
};

// Auto-format time input: inserts ':' after 2 digits (e.g., "1157" → "11:57")
const formatTimeInput = (value) => {
  const digits = value.replace(/[^\d]/g, '');
  if (digits.length <= 2) return digits;
  return digits.slice(0, 2) + ':' + digits.slice(2, 4);
};

// Get display name from field key
const getServiceDisplayName = (fieldKey) => {
  const nameMap = {
    "fuel_request": "Fuel",
    "taxi_request": "Taxi",
    "catering_request": "Catering",
    "gpu_request": "GPU",
    "wc_service_request": "WC",
    "potable_water_request": "Water",
    "coffee_request": "Coffee",
    "ice_request": "ICE",
    "dishes_request": "Dishes",
    "laundry_request": "Laundry",
    "asu_request": "ASU",
    "de_icing_request": "DE-ICING",
    "detax_request": "DETAX",
    "customs_request": "CUSTOMS",
    "de_tax___customs_request": "DETAX/CUSTOMS",
    "hangar_request": "HANGAR",
    "towing_request": "TOWING",
    "weapon_on_board": "⚠️ WEAPON",
    "wchr_request": "♿ WCHR",
    "pmr_request": "♿ PMR",
  };
  return nameMap[fieldKey] || fieldKey.replace(/_request$/, "").replace(/_/g, " ").toUpperCase();
};

export default function GlobalPlanningPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flights, setFlights] = useState([]);
  const [allFlights, setAllFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "today");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("registration") || "");
  const [filters, setFilters] = useState({
    year: "2026",
    month: "",
    service: "",
    flightType: ""
  });
  const [operators, setOperators] = useState([]);
  const [rotationDialogOpen, setRotationDialogOpen] = useState(false);
  const [rotationFlights, setRotationFlights] = useState({ arrival: null, localDeparture: null, localArrival: null, departure: null });
  const [rotationLoading, setRotationLoading] = useState(false);
  
  // Calendar date picker state
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [flightDates, setFlightDates] = useState(new Set()); // Dates that have flights
  
  // Aircraft specs dialog state
  const [aircraftDialogOpen, setAircraftDialogOpen] = useState(false);
  const [aircraftSpecs, setAircraftSpecs] = useState(null);
  const [apronCompatibility, setApronCompatibility] = useState(null);
  const [restrictionMap, setRestrictionMap] = useState({});
  const [aircraftLoading, setAircraftLoading] = useState(false);
  
  // Inline editing state
  const [editingField, setEditingField] = useState(null); // { flightId, field }
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Flight tracking state - uses live cached endpoint with 60s auto-poll
  const [flightTracking, setFlightTracking] = useState({});
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [lastTrackingPoll, setLastTrackingPoll] = useState(null);
  
  // Notes dialog state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesFlightId, setNotesFlightId] = useState(null);
  const [notesValue, setNotesValue] = useState("");
  
  // Edit Flight dialog state
  const [editFlightDialogOpen, setEditFlightDialogOpen] = useState(false);
  const [editFlightData, setEditFlightData] = useState(null);
  
  // Aircraft Change dialog state (for rotation - update both movements)
  const [aircraftChangeDialogOpen, setAircraftChangeDialogOpen] = useState(false);
  const [aircraftChangeData, setAircraftChangeData] = useState({
    registration: "",
    // Arrival (Inbound) details
    arrival_flight_number: "",
    arrival_origin: "",
    arrival_destination: "",
    arrival_date: "",
    arrival_time: "",
    arrival_pax_in: 0,
    arrival_pax_out: 0,
    // Departure (Outbound) details
    departure_flight_number: "",
    departure_origin: "",
    departure_destination: "",
    departure_date: "",
    departure_time: "",
    departure_pax_in: 0,
    departure_pax_out: 0
  });
  
  // Add Flight dialog state (for adding inbound/outbound from rotation)
  const [addFlightDialogOpen, setAddFlightDialogOpen] = useState(false);
  const [addFlightType, setAddFlightType] = useState(""); // "inbound" or "outbound"
  const [addFlightPrefill, setAddFlightPrefill] = useState({});
  const [newFlightData, setNewFlightData] = useState({
    flight_date: "",
    flight_number: "",
    registration: "",
    time: "",
    pilot_in_command: "",
    crew_number: 2,
    origin: "",
    origin_name: "",
    destination: "",
    destination_name: "",
    flight_type: "Passenger",
    aircraft_type: "",
    mtow: 0,
    operator: "",
    pax_in: 0,
    pax_out: 0,
    parking_position: "",
    notes: "",
    fuel_request: false,
    taxi_request: false,
    catering_request: false,
    wc_service_request: false,
    potable_water_request: false,
    gpu_request: false,
    coffee_request: false,
    ice_request: false,
    dishes_request: false,
    laundry_request: false,
    asu_request: false,
    de_icing_request: false
  });
  
  // Dynamic services state
  const [serviceOptions, setServiceOptions] = useState([]);
  const [services, setServices] = useState([]);  // Full services list for rendering badges
  
  // Sort state
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"
  const [manualSort, setManualSort] = useState(false); // true once user clicks the sort toggle

  // How many flight cards to render at once (prevents freezing/crash on large
  // result sets like "Past Flights" at production scale). "Load more" reveals more.
  const PAGE_SIZE = 200;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    fetchFlights();
    setManualSort(false); // reset to "nearest first" default when switching tab/date
    setVisibleCount(PAGE_SIZE); // reset pagination when the result set changes
  }, [activeTab, filters, selectedDate]);

  // Reset pagination when the search query changes so results start from the top.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery]);

  // Heavy full-dataset fetch (rotation pool + calendar dates) runs ONCE on mount,
  // not on every tab/filter switch — reloading thousands of flights each time was
  // a major cause of navigation slowness/crashes on this page.
  useEffect(() => {
    fetchAllFlights();
    fetchServices();
    fetchRestrictions();
  }, []);

  const [restrictions, setRestrictions] = useState([]);
  const fetchRestrictions = async () => {
    try {
      const response = await getOperationalRestrictions();
      setRestrictions(response.data.restrictions || []);
    } catch (error) {
      console.error("Failed to fetch operational restrictions:", error);
    }
  };

  // Returns the first operational restriction whose active window covers a
  // flight's scheduled local date+time (or null). Handles all-day, same-day
  // time windows, and overnight windows that cross midnight.
  const addDaysStr = (dateStr, n) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + n);
    return dt.toISOString().slice(0, 10);
  };

  const restrictionCoversFlight = (r, dateStr, timeStr) => {
    const st = (r.start_time || "").trim();
    const et = (r.end_time || "").trim();
    const inRange = (d) => r.start_date <= d && d <= r.end_date;
    if (!st || !et) return inRange(dateStr);
    const t = (timeStr && /^\d{2}:\d{2}/.test(timeStr)) ? timeStr.slice(0, 5) : null;
    if (!t) return inRange(dateStr); // no scheduled time -> flag to be safe
    if (st < et) return inRange(dateStr) && t >= st && t <= et; // same-day window
    // overnight window (crosses midnight)
    if (inRange(dateStr) && t >= st) return true;                 // evening portion
    if (inRange(addDaysStr(dateStr, -1)) && t <= et) return true; // morning after a restriction night
    return false;
  };

  const restrictionForFlight = (flight) => {
    const d = flight.flight_date;
    if (!d) return null;
    return restrictions.find(r => restrictionCoversFlight(r, d, flight.time)) || null;
  };
  
  const fetchServices = async () => {
    try {
      const response = await getServices();
      // Full services list for badge rendering
      setServices(response.data.filter(s => s.is_active));
      // Only show active services in filters - include icon for display
      const activeServices = response.data
        .filter(s => s.is_active)
        .map(s => ({ id: s.field_key, label: s.name, icon: s.icon }));
      setServiceOptions(activeServices);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    }
  };

  const fetchFlights = async () => {
    setLoading(true);
    try {
      // If a specific date is selected, use "specific_date" status
      const params = selectedDate 
        ? { status: "specific_date", specific_date: format(selectedDate, "yyyy-MM-dd") }
        : { status: activeTab };
      
      if (filters.year) params.year = parseInt(filters.year);
      if (filters.month) params.month = parseInt(filters.month);
      if (filters.operator) params.operator = filters.operator;

      const response = await getFlights(params);
      setFlights(response.data);

      // Fetch apron restriction status for the visible aircraft types (batch)
      const types = [...new Set(response.data.map(f => f.aircraft_type).filter(Boolean))];
      if (types.length > 0) {
        getApronCompatibilityBatch(types)
          .then(res => setRestrictionMap(prev => ({ ...prev, ...(res.data?.results || {}) })))
          .catch(err => console.error("Failed to fetch apron restrictions:", err));
      }

      const uniqueOperators = [...new Set(response.data.map(f => f.operator).filter(Boolean))];
      setOperators(uniqueOperators);
    } catch (error) {
      console.error("Failed to fetch flights:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFlights = async () => {
    try {
      // Fetch all flights to find rotations and to get flight dates for calendar
      const response = await getFlights({});
      setAllFlights(response.data);
      
      // Extract unique dates that have flights for calendar highlighting
      const dates = new Set(response.data.map(f => f.flight_date));
      setFlightDates(dates);
    } catch (error) {
      console.error("Failed to fetch all flights:", error);
    }
  };

  // Fetch live tracking data from cached endpoint (background job updates every 60s)
  const fetchLiveTracking = async () => {
    if (activeTab !== "today") {
      setFlightTracking({});
      return;
    }
    
    setTrackingLoading(true);
    try {
      const response = await getLiveTracking();
      const trackingFlights = response.data?.flights || {};
      setFlightTracking(trackingFlights);
      setLastTrackingPoll(new Date().toLocaleTimeString());
    } catch (error) {
      console.error("Failed to fetch live tracking:", error);
    } finally {
      setTrackingLoading(false);
    }
  };

  // Auto-poll tracking every 60 seconds for today's flights (arrivals AND departures)
  useEffect(() => {
    if (activeTab === "today" && flights.length > 0) {
      fetchLiveTracking();
      const interval = setInterval(fetchLiveTracking, 60000); // 60 seconds
      return () => clearInterval(interval);
    } else {
      setFlightTracking({});
    }
  }, [flights, activeTab]);

  const findRotation = async (flight) => {
    setRotationLoading(true);
    setRotationFlights({ arrival: null, localDeparture: null, localArrival: null, departure: null });
    setRotationDialogOpen(true);
    const registration = flight.registration;

    // Always work from the freshest data so a just-created rotation is included.
    // Filter server-side by registration so we get ALL legs regardless of year
    // (the unfiltered endpoint is capped and can drop recent flights).
    let pool = allFlights;
    try {
      const response = await getFlights({ registration });
      pool = response.data;
    } catch (error) {
      console.error("Failed to refresh flights for rotation:", error);
    }

    // Find ALL flights with same registration (exclude canceled)
    const sameRegFlights = pool.filter(f =>
      f.registration === registration && f.status !== "canceled"
    );

    // Sort by date and time
    const sortedFlights = sameRegFlights.sort((a, b) => {
      const dateA = new Date(`${a.flight_date}T${a.time || '00:00'}`);
      const dateB = new Date(`${b.flight_date}T${b.time || '00:00'}`);
      return dateA - dateB;
    });

    // Anchor everything to the CLICKED flight's date/time so imported historic
    // legs (previous years) are never matched as the rotation partner.
    const clickedTime = new Date(`${flight.flight_date}T${flight.time || '00:00'}`);

    let arrival = null;
    let localDeparture = null;
    let localArrival = null;
    let departure = null;

    // Helper function to check flight type
    const isLocalFlight = (f) => f.origin?.toUpperCase() === "EBCI" && f.destination?.toUpperCase() === "EBCI";
    const isArrivalFlight = (f) => !isLocalFlight(f) && f.destination?.toUpperCase() === "EBCI";
    const isDepartureFlight = (f) => !isLocalFlight(f) && f.origin?.toUpperCase() === "EBCI";
    const timeOf = (f) => new Date(`${f.flight_date}T${f.time || '00:00'}`);

    // Nearest arrival (inbound) AT or BEFORE the clicked flight
    const arrivalsBefore = sortedFlights.filter(f => isArrivalFlight(f) && timeOf(f) <= clickedTime);
    arrival = arrivalsBefore.length ? arrivalsBefore[arrivalsBefore.length - 1] : null;

    // Find local flights after the arrival (or clicked flight)
    const searchStartTime = arrival ? timeOf(arrival) : clickedTime;
    const localFlights = sortedFlights
      .filter(f => isLocalFlight(f) && timeOf(f) >= searchStartTime)
      .sort((a, b) => timeOf(a) - timeOf(b));

    if (localFlights.length >= 1) localDeparture = localFlights[0];
    if (localFlights.length >= 2) localArrival = localFlights[1];

    // Nearest departure (outbound) AT or AFTER the local flights / clicked flight
    const searchAfterTime = localArrival
      ? timeOf(localArrival)
      : localDeparture
        ? timeOf(localDeparture)
        : clickedTime;
    const departuresAfter = sortedFlights.filter(f => isDepartureFlight(f) && timeOf(f) >= searchAfterTime);
    departure = departuresAfter.length ? departuresAfter[0] : null;

    setRotationFlights({ arrival, localDeparture, localArrival, departure });
    setRotationLoading(false);
  };

  const handleEditFlight = (flightId) => {
    // Find the flight data and open edit dialog on this page
    const flight = allFlights.find(f => f.id === flightId) || flights.find(f => f.id === flightId);
    if (flight) {
      setEditFlightData({ ...flight });
      setRotationDialogOpen(false);
      setEditFlightDialogOpen(true);
    }
  };
  
  // Save edited flight
  const saveEditedFlight = async () => {
    if (!editFlightData) return;
    
    setSaving(true);
    try {
      const response = await updateFlight(editFlightData.id, editFlightData);
      
      // Show parking sync/warning messages
      if (response.data?.parking_synced_to) {
        toast.success(`Parking synced to departure flight ${response.data.parking_synced_to}`);
      }
      if (response.data?.parking_warning) {
        toast.warning(response.data.parking_warning);
      }
      
      // Update local state
      setFlights(prev => prev.map(f => 
        f.id === editFlightData.id ? { ...f, ...editFlightData } : f
      ));
      setAllFlights(prev => prev.map(f => 
        f.id === editFlightData.id ? { ...f, ...editFlightData } : f
      ));
      
      toast.success("Flight updated successfully");
      setEditFlightDialogOpen(false);
      setEditFlightData(null);
      
      // Refresh to get synced changes
      fetchFlights();
      fetchAllFlights();
    } catch (error) {
      console.error("Failed to update flight:", error);
      toast.error("Failed to update flight");
    } finally {
      setSaving(false);
    }
  };
  
  // Open Aircraft Change dialog to update both movements in rotation
  const openAircraftChangeDialog = () => {
    const arrival = rotationFlights.arrival;
    const departure = rotationFlights.departure;
    
    setAircraftChangeData({
      registration: arrival?.registration || departure?.registration || "",
      // Arrival (Inbound) details
      arrival_flight_number: arrival?.flight_number || "",
      arrival_origin: arrival?.origin || "",
      arrival_destination: arrival?.destination || "EBCI",
      arrival_date: arrival?.flight_date || "",
      arrival_time: arrival?.time || "",
      arrival_pax_in: arrival?.pax_in || 0,
      arrival_pax_out: arrival?.pax_out || 0,
      // Departure (Outbound) details
      departure_flight_number: departure?.flight_number || "",
      departure_origin: departure?.origin || "EBCI",
      departure_destination: departure?.destination || "",
      departure_date: departure?.flight_date || "",
      departure_time: departure?.time || "",
      departure_pax_in: departure?.pax_in || 0,
      departure_pax_out: departure?.pax_out || 0
    });
    setAircraftChangeDialogOpen(true);
  };

  // Handle registration change in Aircraft Change dialog with auto-lookup
  const handleAircraftChangeRegistration = async (value) => {
    const reg = value.toUpperCase();
    setAircraftChangeData(prev => ({...prev, registration: reg}));
    
    if (reg.length >= 3) {
      try {
        const response = await lookupAircraft(reg);
        if (response.data) {
          const aircraft = response.data;
          toast.success(`Found: ${aircraft.aircraft_type || reg} — specs will be updated on save`);
        }
      } catch (e) {
        // No match found, ignore
      }
    }
  };
  
  // Save Aircraft Change - update both movements
  const saveAircraftChange = async () => {
    setSaving(true);
    try {
      const updates = [];
      
      // Update arrival flight if exists
      if (rotationFlights.arrival) {
        const arrivalUpdate = {
          ...rotationFlights.arrival,
          flight_number: aircraftChangeData.arrival_flight_number,
          registration: aircraftChangeData.registration,
          origin: aircraftChangeData.arrival_origin,
          destination: aircraftChangeData.arrival_destination,
          flight_date: aircraftChangeData.arrival_date,
          time: aircraftChangeData.arrival_time,
          pax_in: parseInt(aircraftChangeData.arrival_pax_in) || 0,
          pax_out: parseInt(aircraftChangeData.arrival_pax_out) || 0
        };
        updates.push(updateFlight(rotationFlights.arrival.id, arrivalUpdate));
      }
      
      // Update departure flight if exists
      if (rotationFlights.departure) {
        const departureUpdate = {
          ...rotationFlights.departure,
          flight_number: aircraftChangeData.departure_flight_number,
          registration: aircraftChangeData.registration,
          origin: aircraftChangeData.departure_origin,
          destination: aircraftChangeData.departure_destination,
          flight_date: aircraftChangeData.departure_date,
          time: aircraftChangeData.departure_time,
          pax_in: parseInt(aircraftChangeData.departure_pax_in) || 0,
          pax_out: parseInt(aircraftChangeData.departure_pax_out) || 0
        };
        updates.push(updateFlight(rotationFlights.departure.id, departureUpdate));
      }
      
      // Update local flights if exists (keep registration only)
      if (rotationFlights.localDeparture) {
        const localDepUpdate = {
          ...rotationFlights.localDeparture,
          registration: aircraftChangeData.registration
        };
        updates.push(updateFlight(rotationFlights.localDeparture.id, localDepUpdate));
      }
      
      if (rotationFlights.localArrival) {
        const localArrUpdate = {
          ...rotationFlights.localArrival,
          registration: aircraftChangeData.registration
        };
        updates.push(updateFlight(rotationFlights.localArrival.id, localArrUpdate));
      }
      
      await Promise.all(updates);
      
      // Refresh flights data
      await fetchFlights();
      await fetchAllFlights();
      
      toast.success("Aircraft change applied to all movements");
      setAircraftChangeDialogOpen(false);
      setRotationDialogOpen(false);
    } catch (error) {
      console.error("Failed to apply aircraft change:", error);
      toast.error("Failed to apply aircraft change");
    } finally {
      setSaving(false);
    }
  };
  
  // Open Add Flight dialog with prefilled aircraft info
  const openAddFlightDialog = (type, registration, operator, aircraftType, mtow) => {
    setAddFlightType(type);
    setAddFlightPrefill({ registration, operator, aircraftType, mtow });
    
    // Reset form with prefilled data
    setNewFlightData({
      flight_date: "",
      flight_number: "",
      registration: registration || "",
      time: "",
      pilot_in_command: "",
      crew_number: 2,
      origin: type === "inbound" ? "" : "EBCI",
      origin_name: type === "inbound" ? "" : "Brussels South Charleroi",
      destination: type === "inbound" ? "EBCI" : "",
      destination_name: type === "inbound" ? "Brussels South Charleroi" : "",
      flight_type: "Passenger",
      aircraft_type: aircraftType || "",
      mtow: mtow || 0,
      operator: operator || "",
      pax_in: 0,
      pax_out: 0,
      parking_position: "",
      notes: "",
      fuel_request: false,
      taxi_request: false,
      catering_request: false,
      wc_service_request: false,
      potable_water_request: false,
      gpu_request: false,
      coffee_request: false,
      ice_request: false,
      dishes_request: false,
      laundry_request: false,
      asu_request: false
    });
    
    setAddFlightDialogOpen(true);
    setRotationDialogOpen(false);
  };
  
  // Save new flight
  const saveNewFlight = async () => {
    const missing = [];
    if (!newFlightData.flight_date) {
      missing.push("Date");
    } else {
      const d = new Date(newFlightData.flight_date + "T00:00:00");
      if (isNaN(d.getTime())) {
        toast.error("Invalid date - please check the date entered");
        return;
      }
    }
    if (!newFlightData.registration) missing.push("Registration");
    
    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.join(", ")}`);
      return;
    }
    
    setSaving(true);
    try {
      const response = await createFlight(newFlightData);
      
      // Add to local state
      setFlights(prev => [...prev, response.data]);
      setAllFlights(prev => [...prev, response.data]);
      
      toast.success(`${addFlightType === "inbound" ? "Inbound" : "Outbound"} flight created successfully`);
      setAddFlightDialogOpen(false);
      setNewFlightData({});
      
      // Refresh flights
      fetchFlights();
      fetchAllFlights();
    } catch (error) {
      console.error("Failed to create flight:", error);
      toast.error("Failed to create flight");
    } finally {
      setSaving(false);
    }
  };

  // Airport auto-recognition handler for new flight origin
  const handleNewFlightOriginChange = async (value) => {
    setNewFlightData(prev => ({...prev, origin: value.toUpperCase()}));
    
    if (value.length >= 3) {
      try {
        const response = await lookupAirport(value);
        if (response.data) {
          const airport = response.data;
          setNewFlightData(prev => ({
            ...prev,
            origin: value.toUpperCase(),
            origin_name: airport.airport_name || "",
            origin_country: airport.country || "",
            origin_schengen: airport.schengen !== false
          }));
          const schengenStatus = airport.schengen ? "✓ Schengen" : "⚠ Non-Schengen";
          toast.success(`Origin: ${airport.airport_name || airport.icao_code} (${schengenStatus})`);
        }
      } catch (e) {
        // Reset airport info if not found
        setNewFlightData(prev => ({
          ...prev,
          origin: value.toUpperCase(),
          origin_name: "",
          origin_country: "",
          origin_schengen: true
        }));
      }
    }
  };

  // Airport auto-recognition handler for new flight destination
  const handleNewFlightDestinationChange = async (value) => {
    setNewFlightData(prev => ({...prev, destination: value.toUpperCase()}));
    
    if (value.length >= 3) {
      try {
        const response = await lookupAirport(value);
        if (response.data) {
          const airport = response.data;
          setNewFlightData(prev => ({
            ...prev,
            destination: value.toUpperCase(),
            destination_name: airport.airport_name || "",
            destination_country: airport.country || "",
            destination_schengen: airport.schengen !== false
          }));
          const schengenStatus = airport.schengen ? "✓ Schengen" : "⚠ Non-Schengen";
          toast.success(`Destination: ${airport.airport_name || airport.icao_code} (${schengenStatus})`);
        }
      } catch (e) {
        // Reset airport info if not found
        setNewFlightData(prev => ({
          ...prev,
          destination: value.toUpperCase(),
          destination_name: "",
          destination_country: "",
          destination_schengen: true
        }));
      }
    }
  };

  // Aircraft auto-recognition handler for new flight
  const handleNewFlightRegistrationChange = async (value) => {
    setNewFlightData(prev => ({...prev, registration: value.toUpperCase()}));
    
    if (value.length >= 3) {
      try {
        const response = await lookupAircraft(value);
        if (response.data) {
          const aircraft = response.data;
          const filledFields = [];
          const updates = { registration: value.toUpperCase() };
          
          if (aircraft.aircraft_type) {
            updates.aircraft_type = aircraft.aircraft_type;
            filledFields.push("Aircraft Type");
          }
          if (aircraft.operator) {
            updates.operator = aircraft.operator;
            filledFields.push("Operator");
          }
          if (aircraft.mtow && aircraft.mtow > 0) {
            updates.mtow = aircraft.mtow;
            filledFields.push("MTOW");
          }
          if (aircraft.aoc) {
            updates.aoc = aircraft.aoc;
            filledFields.push("AOC");
          }
          
          if (filledFields.length > 0) {
            setNewFlightData(prev => ({...prev, ...updates}));
            toast.success(`Auto-filled: ${filledFields.join(", ")}`);
          }
        }
      } catch (e) {
        // No match found, ignore
      }
    }
  };

  // Airport auto-recognition handler for edit flight dialog
  const handleEditFlightOriginChange = async (value) => {
    setEditFlightData(prev => ({...prev, origin: value.toUpperCase()}));
    
    if (value.length >= 3) {
      try {
        const response = await lookupAirport(value);
        if (response.data) {
          const airport = response.data;
          setEditFlightData(prev => ({
            ...prev,
            origin: value.toUpperCase(),
            origin_name: airport.airport_name || "",
            origin_country: airport.country || "",
            origin_schengen: airport.schengen !== false
          }));
          const schengenStatus = airport.schengen ? "✓ Schengen" : "⚠ Non-Schengen";
          toast.success(`Origin: ${airport.airport_name || airport.icao_code} (${schengenStatus})`);
        }
      } catch (e) {
        setEditFlightData(prev => ({
          ...prev,
          origin: value.toUpperCase(),
          origin_name: "",
          origin_country: "",
          origin_schengen: true
        }));
      }
    }
  };

  // Airport auto-recognition handler for edit flight destination
  const handleEditFlightDestinationChange = async (value) => {
    setEditFlightData(prev => ({...prev, destination: value.toUpperCase()}));
    
    if (value.length >= 3) {
      try {
        const response = await lookupAirport(value);
        if (response.data) {
          const airport = response.data;
          setEditFlightData(prev => ({
            ...prev,
            destination: value.toUpperCase(),
            destination_name: airport.airport_name || "",
            destination_country: airport.country || "",
            destination_schengen: airport.schengen !== false
          }));
          const schengenStatus = airport.schengen ? "✓ Schengen" : "⚠ Non-Schengen";
          toast.success(`Destination: ${airport.airport_name || airport.icao_code} (${schengenStatus})`);
        }
      } catch (e) {
        setEditFlightData(prev => ({
          ...prev,
          destination: value.toUpperCase(),
          destination_name: "",
          destination_country: "",
          destination_schengen: true
        }));
      }
    }
  };

  // Handle registration change in Edit Flight dialog with auto-lookup
  const handleEditFlightRegistrationChange = async (value) => {
    const reg = value.toUpperCase();
    setEditFlightData(prev => ({...prev, registration: reg}));
    
    if (reg.length >= 3) {
      try {
        const response = await lookupAircraft(reg);
        if (response.data) {
          const aircraft = response.data;
          const updates = { registration: reg };
          const filledFields = [];
          
          if (aircraft.aircraft_type) {
            updates.aircraft_type = aircraft.aircraft_type;
            filledFields.push("Aircraft Type");
          }
          if (aircraft.operator) {
            updates.operator = aircraft.operator;
            filledFields.push("Operator");
          }
          if (aircraft.mtow && aircraft.mtow > 0) {
            updates.mtow = aircraft.mtow;
            filledFields.push("MTOW");
          }
          if (aircraft.aoc) {
            updates.aoc = aircraft.aoc;
            filledFields.push("AOC");
          }
          
          if (filledFields.length > 0) {
            setEditFlightData(prev => ({...prev, ...updates}));
            toast.success(`Auto-filled: ${filledFields.join(", ")}`);
          }
        }
      } catch (e) {
        // No match found, ignore
      }
    }
  };

  const handleAircraftClick = async (aircraftType) => {
    if (!aircraftType) return;
    setAircraftLoading(true);
    setAircraftDialogOpen(true);
    setApronCompatibility(null);
    try {
      const response = await getAircraftSpecs(aircraftType);
      setAircraftSpecs(response.data);
    } catch (error) {
      console.error("Failed to fetch aircraft specs:", error);
      setAircraftSpecs({ found: false, aircraft_type: aircraftType, message: "Failed to fetch specifications" });
    } finally {
      setAircraftLoading(false);
    }
    try {
      const aptResp = await getApronCompatibility(aircraftType);
      setApronCompatibility(aptResp.data);
    } catch (error) {
      console.error("Failed to fetch apron compatibility:", error);
    }
  };

  const clearFilters = () => {
    setFilters({ year: "2026", month: "", service: "", flightType: "" });
    setSearchQuery("");
  };
  
  // Inline editing functions
  const startEditing = (flightId, field, currentValue) => {
    setEditingField({ flightId, field });
    setEditValue(currentValue || "");
  };
  
  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
  };
  
  const saveFieldEdit = async () => {
    if (!editingField) return;
    
    // Validate time format if it's a time field
    const isTimeField = ['time', 'atd', 'ata'].includes(editingField.field);
    if (isTimeField && editValue && !/^\d{2}:\d{2}$/.test(editValue)) {
      toast.error("Please enter a valid time (HH:MM)");
      return;
    }
    
    // Check stand compatibility for parking changes
    if (editingField.field === 'parking_position' && editValue) {
      const flight = allFlights.find(f => f.id === editingField.flightId) || flights.find(f => f.id === editingField.flightId);
      const acType = flight?.aircraft_type;
      if (acType && acType !== "Unknown") {
        try {
          const compat = await checkStandCompatibility(editValue, acType);
          if (!compat.data.compatible) {
            toast.error(`Stand ${editValue} is not compatible with ${acType}: ${compat.data.warnings?.join(", ") || "Aircraft too large"}`);
            return;
          } else if (compat.data.warnings?.length > 0) {
            toast.warning(`Warning for ${editValue}: ${compat.data.warnings.join(", ")}`, { duration: 6000 });
          }
        } catch (error) {
          console.error("Compatibility check failed:", error);
        }
      }
    }
    
    setSaving(true);
    try {
      const { flightId, field } = editingField;
      const updateData = { [field]: editValue };
      
      // Handle numeric fields
      if (['pax_in', 'pax_out'].includes(field)) {
        updateData[field] = parseInt(editValue) || 0;
      }
      
      const response = await updateFlight(flightId, updateData);
      
      // Show parking sync/warning messages
      if (response.data?.parking_synced_to) {
        toast.success(`Parking synced to departure flight ${response.data.parking_synced_to}`);
      }
      if (response.data?.parking_warning) {
        toast.warning(response.data.parking_warning);
      }
      
      // Update local state with server response to get auto-updated fields
      const updatedFlight = response.data;
      setFlights(prev => prev.map(f => 
        f.id === flightId ? { ...f, ...updatedFlight } : f
      ));
      setAllFlights(prev => prev.map(f => 
        f.id === flightId ? { ...f, ...updatedFlight } : f
      ));
      
      toast.success("Field updated successfully");
      cancelEditing();
      
      // Refresh to get synced changes
      if (field === 'parking_position' || field === 'registration') {
        fetchFlights();
        fetchAllFlights();
      }
    } catch (error) {
      console.error("Failed to update field:", error);
      toast.error("Failed to update field");
    } finally {
      setSaving(false);
    }
  };
  
  // Notes dialog functions
  const openNotesDialog = (flight) => {
    setNotesFlightId(flight.id);
    setNotesValue(flight.notes || "");
    setNotesDialogOpen(true);
  };
  
  const saveNotes = async () => {
    if (!notesFlightId) return;
    
    setSaving(true);
    try {
      await updateFlight(notesFlightId, { notes: notesValue });
      
      // Update local state
      setFlights(prev => prev.map(f => 
        f.id === notesFlightId ? { ...f, notes: notesValue } : f
      ));
      setAllFlights(prev => prev.map(f => 
        f.id === notesFlightId ? { ...f, notes: notesValue } : f
      ));
      
      toast.success("Notes updated successfully");
      setNotesDialogOpen(false);
      setNotesFlightId(null);
      setNotesValue("");
    } catch (error) {
      console.error("Failed to update notes:", error);
      toast.error("Failed to update notes");
    } finally {
      setSaving(false);
    }
  };
  
  // Render editable field helper
  const renderEditableField = (flight, field, displayValue, label, className = "") => {
    const isEditing = editingField?.flightId === flight.id && editingField?.field === field;
    const isTimeField = ['time', 'atd', 'ata'].includes(field);
    const isUpperField = ['parking_position', 'registration', 'flight_number', 'origin', 'destination'].includes(field);
    
    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <Input
            type={['pax_in', 'pax_out'].includes(field) ? "number" : "text"}
            value={editValue}
            onChange={(e) => {
              let val = e.target.value;
              if (isTimeField) val = formatTimeInput(val);
              else if (isUpperField) val = val.toUpperCase();
              setEditValue(val);
            }}
            placeholder={isTimeField ? "HH:MM" : ""}
            maxLength={isTimeField ? 5 : undefined}
            className={`h-7 w-20 text-center font-mono text-sm ${isTimeField ? "tracking-wider" : ""}`}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveFieldEdit();
              if (e.key === 'Escape') cancelEditing();
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={saveFieldEdit}
            disabled={saving}
            className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
          >
            <Check size={14} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={cancelEditing}
            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
          >
            <X size={14} />
          </Button>
        </div>
      );
    }
    
    return (
      <span 
        className={`cursor-pointer hover:bg-[#d4af37]/20 px-1 rounded transition-colors ${className}`}
        onClick={() => startEditing(flight.id, field, flight[field])}
        title={`Click to edit ${label}`}
      >
        {displayValue}
      </span>
    );
  };

  // Filter flights based on search query
  // Service filter options are now loaded dynamically from the database

  const filteredFlights = flights.filter(flight => {
    // Search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        flight.registration?.toLowerCase().includes(query) ||
        flight.origin?.toLowerCase().includes(query) ||
        flight.destination?.toLowerCase().includes(query) ||
        flight.operator?.toLowerCase().includes(query) ||
        flight.flight_number?.toLowerCase().includes(query) ||
        flight.origin_name?.toLowerCase().includes(query) ||
        flight.destination_name?.toLowerCase().includes(query) ||
        flight.aircraft_type?.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }
    
    // Service filter
    if (filters.service) {
      if (!flight[filters.service]) return false;
    }

    // Flight type filter
    if (filters.flightType) {
      const ft = (flight.flight_type || "").trim();
      if (filters.flightType === "__missing__") {
        if (ft) return false;
      } else if (ft !== filters.flightType) {
        return false;
      }
    }
    
    return true;
  });

  // Distinct flight types present in the current view (for the filter dropdown)
  const flightTypeOptions = [...new Set(flights.map(f => (f.flight_type || "").trim()).filter(Boolean))].sort();
  const hasMissingType = flights.some(f => !(f.flight_type || "").trim());

  // Apply sort direction.
  // Default for All / Current Month / Past tabs: most recent (nearest to now) flights first,
  // as one clean chronological list (newest date on top, oldest at the bottom — no interleaving).
  // The manual sort toggle overrides this.
  const useNearestSort = !manualSort && !selectedDate && ["all", "current_month", "past"].includes(activeTab);
  const sortedFlights = [...filteredFlights].sort((a, b) => {
    const dateA = `${a.flight_date}T${a.time || '00:00'}`;
    const dateB = `${b.flight_date}T${b.time || '00:00'}`;
    if (useNearestSort) {
      return dateB.localeCompare(dateA); // newest / most recent first
    }
    return sortDirection === "asc" ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
  });

  const tabs = [
    { id: "all", label: "All Flights" },
    { id: "current_month", label: "Current Month" },
    { id: "past", label: "Past Flights" },
    { id: "today", label: "Today's Flights" },
    { id: "tomorrow", label: "Tomorrow's Flights" },
    { id: "upcoming", label: "Upcoming Flights" }
  ];

  const years = useAvailableYears();
  const months = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A";
    try {
      return format(parseISO(isoString), "dd MMM yyyy HH:mm");
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="animate-fade-in" data-testid="global-planning-page">
      {/* Combined Sticky Header - Title, Tabs, and Filters all together */}
      <div className="sticky-header-combined">
        {/* Title */}
        <div className="flex items-center justify-between gap-2 mb-3 md:mb-4">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white font-['Outfit'] truncate">Global Planning</h1>
            <p className="hidden md:block text-[#64748b] dark:text-gray-400 mt-1">Overview of all flight operations</p>
          </div>
          <div className="flex items-center gap-2 md:hidden flex-shrink-0">
            <span className="text-xs text-[#64748b] dark:text-gray-400 whitespace-nowrap">{sortedFlights.length} found</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMobileFiltersOpen(v => !v)}
              className="gap-1 border-[#d4af37] text-[#d4af37]"
              data-testid="mobile-filters-toggle"
            >
              <Filter size={14} /> Filters
            </Button>
          </div>
        </div>

        {/* Tab Buttons (collapsible on mobile) */}
        <div className={`${mobileFiltersOpen ? "flex" : "hidden"} md:flex gap-2 mb-3 flex-wrap items-center`}>
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedDate(null); // Clear selected date when switching tabs
              }}
              className={`tab-button ${activeTab === tab.id && !selectedDate ? "active" : ""}`}
              variant="ghost"
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </Button>
          ))}
          
          {/* Calendar Date Picker */}
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`ml-2 gap-2 ${selectedDate ? "bg-[#d4af37] text-white border-[#d4af37] hover:bg-[#b8972f]" : "border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-white"}`}
                data-testid="calendar-picker-btn"
              >
                <CalendarIcon size={16} />
                {selectedDate ? format(selectedDate, "dd MMM yyyy") : "Pick a Date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white dark:bg-[#1e293b]" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  setSelectedDate(date);
                  setCalendarOpen(false);
                }}
                initialFocus
                modifiers={{
                  hasFlights: (date) => {
                    const dateStr = format(date, "yyyy-MM-dd");
                    return flightDates.has(dateStr);
                  }
                }}
                modifiersClassNames={{
                  hasFlights: "day-has-flights"
                }}
                classNames={{
                  day_selected: "bg-[#0f172a] text-white hover:bg-[#1e293b] dark:bg-white dark:text-[#0f172a]",
                  day_today: "border-2 border-[#d4af37]"
                }}
              />
              <div className="px-3 py-2 border-t border-[#e2e8f0] dark:border-[#334155] flex items-center gap-2 text-xs text-[#64748b] dark:text-gray-300">
                <span className="w-3 h-3 rounded-full bg-[#d4af37]"></span>
                <span>Days with flights</span>
              </div>
              {selectedDate && (
                <div className="p-2 border-t border-[#e2e8f0] dark:border-[#334155]">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedDate(null);
                      setCalendarOpen(false);
                    }}
                    className="w-full text-[#64748b] dark:text-gray-400"
                  >
                    Clear Date
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          
          {/* Show selected date badge */}
          {selectedDate && (
            <Badge className="bg-[#d4af37] text-white ml-2">
              Showing: {format(selectedDate, "EEEE, dd MMMM yyyy")}
            </Badge>
          )}
        </div>

        {/* Filter Bar (collapsible on mobile) */}
        <div className={`${mobileFiltersOpen ? "flex" : "hidden"} md:flex items-center gap-3 flex-wrap`}>
          <Filter size={20} className="text-[#64748b]" />
          
          {/* Search Input */}
          <div className="relative w-full md:w-auto">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]" />
            <Input
              placeholder="Search by Registration, Origin, Destination, Operator, Aircraft Type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full md:w-80 bg-white dark:bg-[#1e293b]"
              data-testid="search-input"
            />
          </div>
          
          <Select
            value={filters.year}
            onValueChange={(value) => {
              setFilters(prev => ({ ...prev, year: value }));
              setActiveTab("all");
              setSelectedDate(null);
            }}
          >
            <SelectTrigger className="w-32 bg-white dark:bg-[#1e293b]" data-testid="filter-year">
              <span className="text-xs text-[#64748b] mr-1">Year</span>
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#1e293b]">
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.month}
            onValueChange={(value) => {
              setFilters(prev => ({ ...prev, month: value }));
              setActiveTab("all");
              setSelectedDate(null);
            }}
          >
            <SelectTrigger className="w-36 bg-white dark:bg-[#1e293b]" data-testid="filter-month">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#1e293b]">
              {months.map((month) => (
                <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.service}
            onValueChange={(value) => {
              setFilters(prev => ({ ...prev, service: value }));
              setActiveTab("all");
              setSelectedDate(null);
            }}
          >
            <SelectTrigger className="w-36 bg-white dark:bg-[#1e293b]" data-testid="filter-service">
              <SelectValue placeholder="Service" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#1e293b]">
              {serviceOptions.map((svc) => (
                <SelectItem key={svc.id} value={svc.id}>{svc.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.flightType}
            onValueChange={(value) => {
              setFilters(prev => ({ ...prev, flightType: value }));
              setActiveTab("all");
              setSelectedDate(null);
            }}
          >
            <SelectTrigger className="w-40 bg-white dark:bg-[#1e293b]" data-testid="filter-flight-type">
              <SelectValue placeholder="Flight Type" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#1e293b]">
              {flightTypeOptions.map((ft) => (
                <SelectItem key={ft} value={ft}>{ft}</SelectItem>
              ))}
              {hasMissingType && (
                <SelectItem value="__missing__">Missing Type</SelectItem>
              )}
            </SelectContent>
          </Select>

          {(filters.year || filters.month || filters.service || filters.flightType || searchQuery) && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-[#64748b] hover:text-[#0f172a]"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => { setManualSort(true); setSortDirection(prev => prev === "asc" ? "desc" : "asc"); }}
            className="h-9 px-3 gap-1 border-[#e2e8f0] text-[#64748b] hover:text-[#d4af37] hover:border-[#d4af37]"
            title={`Sort ${sortDirection === "asc" ? "Newest First" : "Oldest First"}`}
            data-testid="sort-toggle-btn"
          >
            {sortDirection === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
            {sortDirection === "asc" ? "Oldest" : "Newest"}
          </Button>

          <div className="ml-auto text-sm text-[#64748b]">
            {sortedFlights.length} flight{sortedFlights.length !== 1 ? "s" : ""} found
          </div>
        </div>
      </div>

      {/* Flights List */}
      <div className="page-content-with-sticky">
      {loading ? (
        <div className="flex items-center justify-center h-[40vh]">
          <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
        </div>
      ) : sortedFlights.length === 0 ? (
        <div className="empty-state">
          <Calendar className="mx-auto" />
          <h3>No Flights Found</h3>
          <p>Try adjusting your filters or add a new flight.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedFlights.slice(0, visibleCount).map((flight, index) => {
            const isLocal = flight.origin?.toUpperCase() === "EBCI" && flight.destination?.toUpperCase() === "EBCI";
            const isDeparture = !isLocal && flight.origin?.toUpperCase() === "EBCI";
            const isArrival = !isLocal && flight.destination?.toUpperCase() === "EBCI";
            
            // For local flights, determine if it's the departure or arrival movement
            let isLocalDeparture = false;
            let isLocalArrival = false;
            if (isLocal) {
              // Find other local flights with same registration on same date
              const sameRegLocalFlights = sortedFlights.filter(f => 
                f.registration === flight.registration &&
                f.flight_date === flight.flight_date &&
                f.origin?.toUpperCase() === "EBCI" && 
                f.destination?.toUpperCase() === "EBCI"
              ).sort((a, b) => (a.time || "00:00").localeCompare(b.time || "00:00"));
              
              if (sameRegLocalFlights.length >= 2) {
                const flightIndex = sameRegLocalFlights.findIndex(f => f.id === flight.id);
                isLocalDeparture = flightIndex === 0; // First one is departure
                isLocalArrival = flightIndex === 1;   // Second one is arrival
              } else {
                isLocalDeparture = true; // Default to departure if only one local flight
              }
            }
            
            return (
            <Card
              key={flight.id}
              className="border-[#e2e8f0] hover:border-[#d4af37]/30 transition-colors bg-white"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`planning-flight-${flight.id}`}
            >
              <CardContent className="p-3 md:p-5">
                {/* Flight Type Badge */}
                <div className="flex items-center gap-2 mb-2 md:mb-3 flex-wrap">
                  {isLocal && (
                    <span className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                      ✈ LOCAL {isLocalArrival ? "ARRIVAL" : "DEPARTURE"}
                    </span>
                  )}
                  {isDeparture && (
                    <span className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                      ✈ DEPARTURE
                    </span>
                  )}
                  {isArrival && (
                    <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                      ✈ ARRIVAL
                    </span>
                  )}
                  <PprBadge flight={flight} />
                  <DerogationBadge flight={flight} />
                  {(() => {
                    const r = restrictionForFlight(flight);
                    if (!r) return null;
                    const sev = (r.severity || "warning").toLowerCase();
                    const color = sev === "critical" ? "bg-red-600" : sev === "info" ? "bg-sky-500" : "bg-amber-500";
                    const hours = (r.start_time && r.end_time)
                      ? `${r.start_time}–${r.end_time}${r.start_time >= r.end_time ? " next day" : ""} local`
                      : "all day";
                    return (
                      <span
                        className={`px-3 py-1 ${color} text-white text-xs font-bold rounded-full flex items-center gap-1`}
                        title={`${r.reason} (${r.start_date} → ${r.end_date}, ${hours})`}
                        data-testid={`flight-restriction-badge-${flight.id}`}
                      >
                        <AlertTriangle size={12} /> RESTRICTION
                      </span>
                    );
                  })()}
                  {!isLocal && !isDeparture && !isArrival && (
                    <span className="px-3 py-1 bg-gray-400 text-white text-xs font-bold rounded-full">
                      TRANSIT
                    </span>
                  )}
                  
                  {/* Flight Tracking - for today's arrivals AND departures */}
                  {flight.registration && activeTab === "today" && (() => {
                    const isEBCIArrival = ["EBCI", "CRL"].includes(flight.destination?.toUpperCase());
                    const isEBCIDeparture = ["EBCI", "CRL"].includes(flight.origin?.toUpperCase());
                    if (!isEBCIArrival && !isEBCIDeparture) return null;
                    
                    const tracking = flightTracking[flight.id];
                    const status = tracking?.tracking_status || flight.tracking_status || "";
                    const eta = tracking?.eta || flight.eta || "";
                    const ata = tracking?.ata || flight.ata || "";
                    const atd = tracking?.atd || flight.atd || "";
                    const source = tracking?.source || "";
                    const found = tracking?.found || false;
                    const distNm = tracking?.distance_nm;
                    const etaMin = tracking?.eta_minutes;
                    
                    // Determine badge style based on status
                    let badgeClass = "bg-gray-100 text-gray-600 border border-gray-300";
                    let badgeIcon = <Radio size={12} />;
                    let badgeText = "Scheduled";
                    
                    if (isEBCIArrival) {
                      if (status === "LANDED" || ata) {
                        badgeClass = "bg-emerald-50 text-emerald-700 border border-emerald-300";
                        badgeIcon = <PlaneLanding size={12} />;
                        badgeText = `Landed${ata ? ` at ${ata}` : ""}`;
                      } else if (status === "APPROACH") {
                        badgeClass = "bg-orange-100 text-orange-700 border border-orange-300 animate-pulse";
                        badgeIcon = <PlaneLanding size={12} />;
                        badgeText = `Approach${eta ? ` - ETA ${eta}` : ""}${etaMin ? ` (${Math.round(etaMin)}min)` : ""}`;
                      } else if (status === "AIRBORNE") {
                        badgeClass = "bg-sky-100 text-sky-700 border border-sky-300";
                        badgeIcon = <Plane size={12} />;
                        badgeText = `Airborne${eta ? ` - ETA ${eta}` : ""}${etaMin ? ` (${Math.round(etaMin)}min)` : ""}`;
                      } else if (status === "NOT_ACTIVE") {
                        badgeClass = "bg-gray-100 text-gray-500 border border-gray-300";
                        badgeIcon = <Clock size={12} />;
                        badgeText = "Not departed yet";
                      } else if (status === "NOT_TRACKED" || (!found && !ata)) {
                        badgeClass = "bg-amber-50 text-amber-700 border border-amber-300";
                        badgeIcon = <AlertTriangle size={12} />;
                        badgeText = "Private - ETA N/A";
                      }
                    } else if (isEBCIDeparture) {
                      if (status === "DEPARTED" || atd) {
                        badgeClass = "bg-blue-100 text-blue-700 border border-blue-300";
                        badgeIcon = <PlaneTakeoff size={12} />;
                        badgeText = `Departed${atd ? ` at ${atd}` : ""}`;
                      } else if (status === "AIRBORNE") {
                        badgeClass = "bg-blue-100 text-blue-700 border border-blue-300";
                        badgeIcon = <PlaneTakeoff size={12} />;
                        badgeText = "Departed";
                      } else if (status === "ON_GROUND_EBCI" || (!atd && !found)) {
                        badgeClass = "bg-gray-100 text-gray-600 border border-gray-300";
                        badgeIcon = <Clock size={12} />;
                        badgeText = "Awaiting departure";
                      }
                    }
                    
                    return (
                      <div className="ml-auto flex items-center gap-2" data-testid={`tracking-badge-${flight.id}`}>
                        {trackingLoading && !status ? (
                          <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            <Radio size={12} className="animate-pulse" />
                            Tracking...
                          </span>
                        ) : (
                          <span 
                            className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${badgeClass}`}
                            title={source ? `Source: ${source}${distNm ? ` | ${distNm}nm` : ""}` : "Awaiting tracking data"}
                          >
                            {badgeIcon}
                            {badgeText}
                          </span>
                        )}
                        {/* FR24 link for manual lookup */}
                        <a
                          href={`https://www.flightradar24.com/${flight.registration?.replace("-", "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-[#f59e0b] text-white hover:bg-[#d97706] transition-colors"
                          title="View on FlightRadar24"
                          data-testid={`fr24-link-${flight.id}`}
                        >
                          <Radio size={10} />
                          FR24
                        </a>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Main Flight Row - Table-like Grid Layout (2-col on mobile, table on desktop) */}
                <div className="grid grid-cols-2 md:grid-cols-[200px_1fr_180px_100px_100px] gap-x-3 gap-y-2 md:gap-4 md:items-center pb-3 md:pb-4 border-b border-[#e2e8f0]">
                  
                  {/* Column 1: Flight Info */}
                  <div className="col-span-2 md:col-span-1 flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isLocal ? "bg-orange-500" :
                      isDeparture ? "bg-blue-500" : 
                      isArrival ? "bg-green-500" : "bg-[#0f172a]"
                    }`}>
                      <Plane size={20} className="text-white" />
                    </div>
                    <div>
                      <a 
                        href={`https://planefinder.net/flight/number/${flight.flight_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`font-mono font-bold text-lg leading-tight hover:underline ${isLocal ? "text-orange-600" : "text-blue-600"}`}
                        data-testid={`flight-number-link-${flight.id}`}
                      >
                        {flight.flight_number}
                      </a>
                      <p className="font-mono text-sm text-[#64748b]">
                        {flight.registration || "N/A"}
                      </p>
                      <Badge 
                        variant="secondary" 
                        className="bg-[#f1f5f9] text-[#64748b] text-xs mt-1 cursor-pointer hover:bg-[#d4af37] hover:text-white transition-colors"
                        onClick={() => handleAircraftClick(flight.aircraft_type)}
                        data-testid={`aircraft-type-${flight.id}`}
                      >
                        {flight.aircraft_type || "N/A"}
                      </Badge>
                      {flight.aircraft_type && (restrictionMap[flight.aircraft_type.toUpperCase()]?.restricted_aprons?.length || 0) > 1 && (
                        <button
                          type="button"
                          onClick={() => handleAircraftClick(flight.aircraft_type)}
                          data-testid={`check-restriction-tag-${flight.id}`}
                          title="This aircraft is restricted on one or more aprons — click the aircraft type to see details"
                          className="mt-1 flex items-center gap-1 rounded-md bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-100 transition-colors animate-pulse"
                        >
                          <AlertTriangle size={11} />
                          <span>Check restriction · click type</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Column 2: Route - Centered */}
                  <div className="col-span-2 md:col-span-1 grid grid-cols-[1fr_60px_1fr] items-center">
                    {/* Origin */}
                    <div className="text-right pr-2">
                      <div className="flex items-center justify-end gap-2">
                        {/* Origin Non-Schengen / Non-EU badge */}
                        {(flight.origin_schengen === false && flight.origin_non_eu) ? (
                          <span className="relative inline-flex">
                            <span className="badge-non-schengen animate-badge-switch-1">NON-SCHENGEN</span>
                            <span className="badge-non-eu absolute inset-0 animate-badge-switch-2">NON-EU</span>
                          </span>
                        ) : flight.origin_schengen === false ? (
                          <span className="badge-non-schengen">NON-SCHENGEN</span>
                        ) : flight.origin_non_eu ? (
                          <span className="badge-non-eu animate-pulse">NON-EU</span>
                        ) : null}
                        <div>
                          <p className="font-bold text-[#0f172a] text-xl">{flight.origin}</p>
                          <p className="text-[10px] text-[#64748b] uppercase">{flight.origin_name || ""}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Arrow */}
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-[2px] bg-[#d4af37]"></div>
                      <Plane size={18} className="text-[#d4af37] mx-1 rotate-90" />
                      <div className="w-4 h-[2px] bg-[#d4af37]"></div>
                    </div>
                    
                    {/* Destination */}
                    <div className="text-left pl-2">
                      <div className="flex items-center justify-start gap-2">
                        <div>
                          <p className="font-bold text-[#0f172a] text-xl">{flight.destination}</p>
                          <p className="text-[10px] text-[#64748b] uppercase">{flight.destination_name || ""}</p>
                        </div>
                        {/* Destination Non-Schengen / Non-EU badge */}
                        {(flight.destination_schengen === false && flight.destination_non_eu) ? (
                          <span className="relative inline-flex">
                            <span className="badge-non-schengen animate-badge-switch-1">NON-SCHENGEN</span>
                            <span className="badge-non-eu absolute inset-0 animate-badge-switch-2">NON-EU</span>
                          </span>
                        ) : flight.destination_schengen === false ? (
                          <span className="badge-non-schengen">NON-SCHENGEN</span>
                        ) : flight.destination_non_eu ? (
                          <span className="badge-non-eu animate-pulse">NON-EU</span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Column 3: Date & Times */}
                  {(() => {
                    const flightIsLocal = flight.origin?.toUpperCase() === "EBCI" && flight.destination?.toUpperCase() === "EBCI";
                    const flightIsDeparture = !flightIsLocal && flight.origin?.toUpperCase() === "EBCI";
                    
                    // For local flights, check if it's arrival or departure movement
                    let localIsArrival = false;
                    if (flightIsLocal) {
                      const sameRegLocalFlights = sortedFlights.filter(f => 
                        f.registration === flight.registration &&
                        f.flight_date === flight.flight_date &&
                        f.origin?.toUpperCase() === "EBCI" && 
                        f.destination?.toUpperCase() === "EBCI"
                      ).sort((a, b) => (a.time || "00:00").localeCompare(b.time || "00:00"));
                      
                      if (sameRegLocalFlights.length >= 2) {
                        const flightIdx = sameRegLocalFlights.findIndex(f => f.id === flight.id);
                        localIsArrival = flightIdx === 1;
                      }
                    }
                    
                    const scheduledLabel = flightIsLocal ? (localIsArrival ? "STA" : "STD") : (flightIsDeparture ? "STD" : "STA");
                    const actualLabel = flightIsLocal ? (localIsArrival ? "ATA" : "ATD") : (flightIsDeparture ? "ATD" : "ATA");
                    const timeColor = flightIsLocal ? "text-orange-600" : (flightIsDeparture ? "text-blue-600" : "text-green-600");
                    
                    // Use ata for arrivals, atd for departures
                    const isArrivalMovement = (!flightIsLocal && !flightIsDeparture) || (flightIsLocal && localIsArrival);
                    const actualTimeField = isArrivalMovement ? 'ata' : 'atd';
                    const actualTimeValue = isArrivalMovement ? (flight.ata || "") : (flight.atd || "");
                    const actualColor = actualTimeValue ? timeColor : "text-[#94a3b8]";
                    // Manual (user-entered) actual time — locked from auto-tracker overwrite
                    const actualIsManual = isArrivalMovement ? !!flight.ata_manual : !!flight.atd_manual;
                    
                    // Show ETA for arrivals if available from tracking
                    const trackingData = flightTracking[flight.id];
                    const etaValue = flight.eta || trackingData?.eta || "";
                    
                    return (
                      <div className="col-span-2 md:col-span-1 text-center">
                        <p className="font-semibold text-[#0f172a] text-sm mb-1">
                          {format(parseISO(flight.flight_date), "dd MMM yyyy")}
                        </p>
                        <div className="flex items-center justify-center gap-4">
                          <div className="text-center">
                            <p className={`text-[10px] font-bold ${timeColor}`}>{scheduledLabel}</p>
                            {renderEditableField(
                              flight, 
                              'time', 
                              flight.time || "--:--", 
                              'Scheduled Time',
                              `font-mono text-lg font-bold ${timeColor}`
                            )}
                          </div>
                          {/* Show ETA between STA and ATA for arrivals when tracking */}
                          {isArrivalMovement && etaValue && !actualTimeValue && activeTab === "today" && (
                            <div className="text-center">
                              <p className="text-[10px] font-bold text-orange-500">ETA</p>
                              <p className="font-mono text-lg font-bold text-orange-500">{etaValue}</p>
                            </div>
                          )}
                          <div className="text-center">
                            <p className={`text-[10px] font-bold ${actualColor} flex items-center justify-center gap-0.5`}>
                              {actualLabel}
                              {actualIsManual && actualTimeValue && (
                                <Lock
                                  size={9}
                                  className="text-amber-500"
                                  data-testid={`manual-time-lock-${flight.id}`}
                                />
                              )}
                            </p>
                            {renderEditableField(
                              flight, 
                              actualTimeField, 
                              actualTimeValue || "--:--", 
                              'Actual Time',
                              `font-mono text-lg font-bold ${actualColor}`
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Column 4: PAX - Editable */}
                  <div className="text-center">
                    <p className="text-[10px] text-[#64748b] uppercase">PAX</p>
                    <div className="flex items-center justify-center gap-2 mt-1">
                      {renderEditableField(flight, 'pax_in', flight.pax_in || 0, 'PAX In', 'text-green-600 font-bold')}
                      <span className="text-[#64748b]">/</span>
                      {renderEditableField(flight, 'pax_out', flight.pax_out || 0, 'PAX Out', 'text-blue-600 font-bold')}
                    </div>
                  </div>

                  {/* Column 5: Parking - Editable */}
                  <div className="text-center">
                    <p className="text-[10px] text-[#64748b] uppercase">Parking</p>
                    {renderEditableField(flight, 'parking_position', flight.parking_position || "-", 'Parking', 'font-bold text-[#0f172a]')}
                  </div>
                </div>

                {/* Services & Details Row */}
                <div className="flex flex-wrap items-center justify-between pt-2 md:pt-3 gap-2 md:gap-4">
                  {/* Services - Dynamic rendering from services state */}
                  <div className="flex flex-wrap gap-1">
                    {services.map((service) => {
                      const fieldKey = service.field_key;
                      // Check if this service is enabled for this flight
                      if (!flight[fieldKey]) return null;
                      
                      const colorClass = serviceColorMap[fieldKey] || "bg-gray-500 text-white";
                      const displayName = getServiceDisplayName(fieldKey);
                      const icon = serviceIconMap[service.icon] || "";
                      
                      return (
                        <Badge key={fieldKey} className={`${colorClass} text-xs`}>
                          {icon && <span className="mr-1">{icon}</span>}{displayName}
                        </Badge>
                      );
                    })}
                    {/* Also check for any services not in the services list but present on flight */}
                    {Object.keys(flight).filter(key => 
                      key.endsWith('_request') && 
                      flight[key] === true && 
                      !services.find(s => s.field_key === key)
                    ).map(fieldKey => {
                      const colorClass = serviceColorMap[fieldKey] || "bg-gray-500 text-white";
                      const displayName = getServiceDisplayName(fieldKey);
                      
                      return (
                        <Badge key={fieldKey} className={`${colorClass} text-xs`}>
                          {displayName}
                        </Badge>
                      );
                    })}
                    {/* Special case: weapon_on_board doesn't have _request suffix */}
                    {flight.weapon_on_board && !services.find(s => s.field_key === 'weapon_on_board') && (
                      <Badge className="bg-red-600 text-white text-xs animate-pulse font-bold">⚠️ WEAPON</Badge>
                    )}
                  </div>

                  {/* Additional Info - Centered with bigger font */}
                  <div className="flex items-center justify-center gap-2 md:gap-6 text-xs md:text-base text-[#0f172a] font-medium flex-1 flex-wrap">
                    <span><Briefcase size={16} className="inline mr-1" />{(flight.flight_type || "").trim() ? flight.flight_type : <span className="text-amber-500 font-medium">Missing Type</span>}</span>
                    {(flight.flight_type || "").trim().toLowerCase() === "cargo" && (
                      <span className="flex items-center gap-1 text-amber-600 font-semibold" data-testid="cargo-kg-label"><Scale size={16} className="inline" />{flight.cargo_kg || 0} kg</span>
                    )}
                    <span><User size={16} className="inline mr-1" />{flight.pilot_in_command || "TBA"}</span>
                    <span>Crew: {flight.crew_number || 0}</span>
                    <span className="font-semibold">{flight.operator || "N/A"}</span>
                    {flight.aoc && <span className="text-[#d4af37]">AOC: {flight.aoc}</span>}
                  </div>
                  
                  {/* Edit Buttons - Right Side */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openNotesDialog(flight)}
                      className="flex items-center gap-1 text-[#64748b] border-[#e2e8f0] hover:bg-yellow-50 hover:text-yellow-700 hover:border-yellow-400"
                    >
                      <FileText size={14} />
                      Notes
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditFlight(flight.id)}
                      className="flex items-center gap-1 text-[#0f172a] border-[#d4af37] hover:bg-[#d4af37] hover:text-white"
                    >
                      <Pencil size={14} />
                      Edit Flight
                    </Button>
                  </div>
                </div>

                {/* Notes */}
                {flight.notes && (
                  <div className="mt-3 bg-yellow-50 p-3 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors" onClick={() => openNotesDialog(flight)}>
                    <p className="text-xs text-[#64748b] mb-1 font-bold uppercase">📝 Notes <span className="text-yellow-600">(click to edit)</span></p>
                    <p className="text-sm text-[#0f172a] font-medium line-clamp-2 md:line-clamp-none">{flight.notes}</p>
                  </div>
                )}

                {/* Created/Edited Info */}
                <div className="mt-2 md:mt-3 pt-2 md:pt-3 border-t border-[#e2e8f0] flex flex-wrap items-center justify-between gap-2 md:gap-4 text-xs">
                  <div className="hidden md:flex flex-wrap gap-6">
                    <div className="flex items-center gap-2 text-[#64748b]">
                      <User size={14} />
                      <span>
                        <strong>Created by:</strong> {flight.created_by || "N/A"}
                      </span>
                      <Clock size={14} className="ml-2" />
                      <span>{formatDateTime(flight.created_at)}</span>
                    </div>
                    
                    {flight.updated_by && (
                      <div className="flex items-center gap-2 text-[#64748b]">
                        <Pencil size={14} />
                        <span>
                          <strong>Edited by:</strong> {flight.updated_by}
                        </span>
                        <Clock size={14} className="ml-2" />
                        <span>{formatDateTime(flight.updated_at)}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Show Rotation + Show History Buttons */}
                  <div className="flex items-center gap-2">
                    <FlightHistoryButton flight={flight} variant="full" />
                    <MvtButton flight={flight} variant="full" />
                    {(isDeparture || isLocalDeparture) && (
                      <BriefingButton flight={flight} variant="full" />
                    )}
                    {(isDeparture || isLocalDeparture) && (
                      <>
                        <FlightDocButton flight={flight} docType="manifest" variant="full" />
                        <FlightDocButton flight={flight} docType="gendec" variant="full" />
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => findRotation(flight)}
                      className="flex items-center gap-2 text-[#0f172a] border-[#d4af37] hover:bg-[#d4af37] hover:text-white"
                      data-testid={`show-rotation-${flight.id}`}
                    >
                      <RotateCcw size={14} />
                      Show Rotation
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
          {sortedFlights.length > visibleCount && (
            <div className="flex flex-col items-center gap-2 py-4">
              <span className="text-sm text-[#64748b] dark:text-gray-400">
                Showing {visibleCount} of {sortedFlights.length} flights
              </span>
              <Button
                variant="outline"
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                data-testid="load-more-flights-btn"
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Rotation Dialog */}
      <Dialog open={rotationDialogOpen} onOpenChange={setRotationDialogOpen}>
        <DialogContent className="max-w-4xl bg-white dark:bg-[#1e293b] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] dark:text-white flex items-center gap-2">
              <RotateCcw size={24} className="text-[#d4af37]" />
              Flight Rotation
              {(rotationFlights.arrival?.registration || rotationFlights.departure?.registration || rotationFlights.localDeparture?.registration) && (
                <Badge className="ml-2 bg-[#0f172a] text-white text-sm">
                  {rotationFlights.arrival?.registration || rotationFlights.localDeparture?.registration || rotationFlights.departure?.registration}
                </Badge>
              )}
            </DialogTitle>
            {/* Aircraft Change Button - Update both movements */}
            {(rotationFlights.arrival || rotationFlights.departure) && (
              <Button
                onClick={openAircraftChangeDialog}
                className="mt-2 bg-[#d4af37] hover:bg-[#b8972f] text-white font-semibold"
                data-testid="aircraft-change-btn"
              >
                <ArrowUpDown size={16} className="mr-2" />
                Aircraft Change
              </Button>
            )}
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {/* 1. INBOUND - Arrival Flight */}
            <div className={`border-2 rounded-lg p-4 ${rotationFlights.arrival ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-300 bg-gray-50 dark:bg-gray-800/50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-white text-xs font-bold rounded-full ${rotationFlights.arrival ? 'bg-green-500' : 'bg-gray-400'}`}>
                    1. INBOUND
                  </span>
                  <span className={`font-semibold ${rotationFlights.arrival ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>Arrival to EBCI</span>
                </div>
                {rotationFlights.arrival && (
                  <Button
                    size="sm"
                    onClick={() => handleEditFlight(rotationFlights.arrival.id)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              
              {rotationFlights.arrival ? (
                <div className="grid grid-cols-4 gap-4 items-center">
                  <div>
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Flight</p>
                    <a 
                      href={`https://planefinder.net/flight/number/${rotationFlights.arrival.flight_number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono font-bold text-lg hover:underline text-green-600 dark:text-green-400"
                    >
                      {rotationFlights.arrival.flight_number}
                    </a>
                    <p className="text-sm text-[#64748b] dark:text-gray-400">{rotationFlights.arrival.aircraft_type}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Route</p>
                    <p className="font-bold text-lg text-green-600 dark:text-green-400">
                      {rotationFlights.arrival.origin} → {rotationFlights.arrival.destination}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Date & Time</p>
                    <p className="font-semibold dark:text-white">{format(parseISO(rotationFlights.arrival.flight_date), "dd MMM yyyy")}</p>
                    <p className="font-mono text-green-600 dark:text-green-400 font-bold">STA: {rotationFlights.arrival.time}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">PAX</p>
                    <p className="font-semibold dark:text-white">↓{rotationFlights.arrival.pax_in || 0} / ↑{rotationFlights.arrival.pax_out || 0}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-gray-500 dark:text-gray-400 italic">No inbound flight found</p>
                  <Button
                    size="sm"
                    onClick={() => openAddFlightDialog(
                      "inbound",
                      rotationFlights.departure?.registration || rotationFlights.localDeparture?.registration,
                      rotationFlights.departure?.operator || rotationFlights.localDeparture?.operator,
                      rotationFlights.departure?.aircraft_type || rotationFlights.localDeparture?.aircraft_type,
                      rotationFlights.departure?.mtow || rotationFlights.localDeparture?.mtow
                    )}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus size={14} className="mr-1" />
                    <PlaneLanding size={14} className="mr-1" />
                    Add Inbound
                  </Button>
                </div>
              )}
            </div>

            {/* Connection Arrow */}
            <div className="flex justify-center">
              <div className="flex flex-col items-center text-[#d4af37]">
                <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                <Plane size={20} className="rotate-180" />
                <div className="w-0.5 h-4 bg-[#d4af37]"></div>
              </div>
            </div>

            {/* 2. LOCAL DEPARTURE - Only show if local departure exists */}
            {rotationFlights.localDeparture && (
              <>
                <div className="border-2 rounded-lg p-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 text-white text-xs font-bold rounded-full bg-orange-500">
                        2. LOCAL DEPARTURE
                      </span>
                      <span className="font-semibold text-orange-700 dark:text-orange-400">Local Flight Takeoff</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleEditFlight(rotationFlights.localDeparture.id)}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      <Pencil size={14} className="mr-1" />
                      Edit
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 items-center">
                    <div>
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Flight</p>
                      <a 
                        href={`https://planefinder.net/flight/number/${rotationFlights.localDeparture.flight_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono font-bold text-lg hover:underline text-orange-600 dark:text-orange-400"
                      >
                        {rotationFlights.localDeparture.flight_number}
                      </a>
                      <p className="text-sm text-[#64748b] dark:text-gray-400">{rotationFlights.localDeparture.aircraft_type}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Route</p>
                      <p className="font-bold text-lg text-orange-600 dark:text-orange-400">
                        {rotationFlights.localDeparture.origin} ⟲ {rotationFlights.localDeparture.destination}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Date & Time</p>
                      <p className="font-semibold dark:text-white">{format(parseISO(rotationFlights.localDeparture.flight_date), "dd MMM yyyy")}</p>
                      <p className="font-mono text-orange-600 dark:text-orange-400 font-bold">STD: {rotationFlights.localDeparture.time}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">PAX</p>
                      <p className="font-semibold dark:text-white">↓{rotationFlights.localDeparture.pax_in || 0} / ↑{rotationFlights.localDeparture.pax_out || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Connection Arrow */}
                <div className="flex justify-center">
                  <div className="flex flex-col items-center text-[#d4af37]">
                    <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                    <Plane size={20} className="rotate-180" />
                    <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                  </div>
                </div>
              </>
            )}

            {/* 3. LOCAL ARRIVAL - Only show if local arrival exists */}
            {rotationFlights.localArrival && (
              <>
                <div className="border-2 rounded-lg p-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 text-white text-xs font-bold rounded-full bg-orange-500">
                        3. LOCAL ARRIVAL
                      </span>
                      <span className="font-semibold text-orange-700 dark:text-orange-400">Local Flight Landing</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleEditFlight(rotationFlights.localArrival.id)}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      <Pencil size={14} className="mr-1" />
                      Edit
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 items-center">
                    <div>
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Flight</p>
                      <a 
                        href={`https://planefinder.net/flight/number/${rotationFlights.localArrival.flight_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono font-bold text-lg hover:underline text-orange-600 dark:text-orange-400"
                      >
                        {rotationFlights.localArrival.flight_number}
                      </a>
                      <p className="text-sm text-[#64748b] dark:text-gray-400">{rotationFlights.localArrival.aircraft_type}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Route</p>
                      <p className="font-bold text-lg text-orange-600 dark:text-orange-400">
                        {rotationFlights.localArrival.origin} ⟲ {rotationFlights.localArrival.destination}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Date & Time</p>
                      <p className="font-semibold dark:text-white">{format(parseISO(rotationFlights.localArrival.flight_date), "dd MMM yyyy")}</p>
                      <p className="font-mono text-orange-600 dark:text-orange-400 font-bold">STA: {rotationFlights.localArrival.time}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">PAX</p>
                      <p className="font-semibold dark:text-white">↓{rotationFlights.localArrival.pax_in || 0} / ↑{rotationFlights.localArrival.pax_out || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Connection Arrow */}
                <div className="flex justify-center">
                  <div className="flex flex-col items-center text-[#d4af37]">
                    <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                    <Plane size={20} className="rotate-180" />
                    <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                  </div>
                </div>
              </>
            )}

            {/* 4. OUTBOUND - Departure Flight */}
            <div className={`border-2 rounded-lg p-4 ${rotationFlights.departure ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 bg-gray-50 dark:bg-gray-800/50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-white text-xs font-bold rounded-full ${rotationFlights.departure ? 'bg-blue-500' : 'bg-gray-400'}`}>
                    4. OUTBOUND
                  </span>
                  <span className={`font-semibold ${rotationFlights.departure ? 'text-blue-700 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>Departure from EBCI</span>
                </div>
                {rotationFlights.departure && (
                  <Button
                    size="sm"
                    onClick={() => handleEditFlight(rotationFlights.departure.id)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              
              {rotationFlights.departure ? (
                <div className="grid grid-cols-4 gap-4 items-center">
                  <div>
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Flight</p>
                    <a 
                      href={`https://planefinder.net/flight/number/${rotationFlights.departure.flight_number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono font-bold text-lg hover:underline text-blue-600 dark:text-blue-400"
                    >
                      {rotationFlights.departure.flight_number}
                    </a>
                    <p className="text-sm text-[#64748b] dark:text-gray-400">{rotationFlights.departure.aircraft_type}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Route</p>
                    <p className="font-bold text-lg text-blue-600 dark:text-blue-400">
                      {rotationFlights.departure.origin} → {rotationFlights.departure.destination}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Date & Time</p>
                    <p className="font-semibold dark:text-white">{format(parseISO(rotationFlights.departure.flight_date), "dd MMM yyyy")}</p>
                    <p className="font-mono text-blue-600 dark:text-blue-400 font-bold">STD: {rotationFlights.departure.time}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">PAX</p>
                    <p className="font-semibold dark:text-white">↓{rotationFlights.departure.pax_in || 0} / ↑{rotationFlights.departure.pax_out || 0}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-gray-500 dark:text-gray-400 italic">No outbound flight found</p>
                  <Button
                    size="sm"
                    onClick={() => openAddFlightDialog(
                      "outbound",
                      rotationFlights.arrival?.registration || rotationFlights.localArrival?.registration,
                      rotationFlights.arrival?.operator || rotationFlights.localArrival?.operator,
                      rotationFlights.arrival?.aircraft_type || rotationFlights.localArrival?.aircraft_type,
                      rotationFlights.arrival?.mtow || rotationFlights.localArrival?.mtow
                    )}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus size={14} className="mr-1" />
                    <PlaneTakeoff size={14} className="mr-1" />
                    Add Outbound
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aircraft Specifications Dialog */}
      <Dialog open={aircraftDialogOpen} onOpenChange={setAircraftDialogOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Plane size={24} className="text-[#d4af37]" />
              Aircraft Specifications
            </DialogTitle>
          </DialogHeader>
          
          {aircraftLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
            </div>
          ) : aircraftSpecs?.found ? (
            <div className="space-y-6 mt-4">
              {/* Aircraft Type Header */}
              <div className="text-center">
                <h2 className="text-2xl font-bold text-[#0f172a]">{aircraftSpecs.aircraft_type}</h2>
              </div>
              
              {/* Aircraft Image */}
              {aircraftSpecs.image_url && (
                <div className="rounded-lg overflow-hidden border border-[#e2e8f0]">
                  <img 
                    src={aircraftSpecs.image_url} 
                    alt={aircraftSpecs.aircraft_type}
                    className="w-full h-48 object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              
              {/* Specifications Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Length */}
                <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]">
                  <div className="flex items-center gap-2 mb-2">
                    <Ruler size={20} className="text-[#d4af37]" />
                    <span className="text-sm text-[#64748b] font-medium">Length</span>
                  </div>
                  <p className="text-2xl font-bold text-[#0f172a]">
                    {aircraftSpecs.specifications.length_m} <span className="text-sm font-normal text-[#64748b]">m</span>
                  </p>
                </div>
                
                {/* Height */}
                <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowUpDown size={20} className="text-[#d4af37]" />
                    <span className="text-sm text-[#64748b] font-medium">Height</span>
                  </div>
                  <p className="text-2xl font-bold text-[#0f172a]">
                    {aircraftSpecs.specifications.height_m} <span className="text-sm font-normal text-[#64748b]">m</span>
                  </p>
                </div>
                
                {/* Wingspan */}
                <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]">
                  <div className="flex items-center gap-2 mb-2">
                    <Plane size={20} className="text-[#d4af37] rotate-90" />
                    <span className="text-sm text-[#64748b] font-medium">Wingspan</span>
                  </div>
                  <p className="text-2xl font-bold text-[#0f172a]">
                    {aircraftSpecs.specifications.wingspan_m} <span className="text-sm font-normal text-[#64748b]">m</span>
                  </p>
                </div>
                
                {/* MTOW */}
                <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]">
                  <div className="flex items-center gap-2 mb-2">
                    <Scale size={20} className="text-[#d4af37]" />
                    <span className="text-sm text-[#64748b] font-medium">MTOW</span>
                  </div>
                  <p className="text-2xl font-bold text-[#0f172a]">
                    {aircraftSpecs.specifications.mtow_kg.toLocaleString()} <span className="text-sm font-normal text-[#64748b]">kg</span>
                  </p>
                </div>
              </div>

              {/* Apron Compatibility */}
              <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]" data-testid="apron-compatibility-section">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={18} className="text-[#d4af37]" />
                  <span className="text-sm text-[#64748b] font-medium">Apron Compatibility</span>
                </div>
                {apronCompatibility?.aprons ? (
                  <div className="flex flex-wrap gap-2">
                    {apronCompatibility.aprons.map((a) => (
                      <div
                        key={a.apron}
                        data-testid={`apron-badge-${a.apron}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border ${
                          a.allowed
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-red-50 text-red-700 border-red-200"
                        }`}
                        title={a.allowed ? `Allowed (${a.allowed_stands.length}/${a.total_stands} stands)` : "Restricted — no stands fit"}
                      >
                        {a.allowed ? <Check size={14} /> : <Ban size={14} />}
                        {a.apron}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-[#94a3b8]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Checking aprons...
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Plane size={48} className="mx-auto text-[#94a3b8] mb-4" />
              <h3 className="text-lg font-semibold text-[#0f172a] mb-2">
                {aircraftSpecs?.aircraft_type || "Unknown Aircraft"}
              </h3>
              <p className="text-[#64748b]">
                Specifications not found in database
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Notes Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <FileText size={24} className="text-yellow-600" />
              Edit Flight Notes
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Textarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              placeholder="Enter flight notes..."
              rows={6}
              className="resize-none"
            />
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setNotesDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={saveNotes}
                disabled={saving}
                className="bg-[#d4af37] hover:bg-[#b8942c] text-white"
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : (
                  <Save size={16} className="mr-2" />
                )}
                Save Notes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Flight Dialog */}
      <Dialog open={editFlightDialogOpen} onOpenChange={setEditFlightDialogOpen}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Pencil size={24} className="text-[#d4af37]" />
              Edit Flight - {editFlightData?.flight_number || ""}
            </DialogTitle>
          </DialogHeader>
          
          {editFlightData && (
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <Label>Flight Date</Label>
                <Input
                  type="date"
                  value={editFlightData.flight_date || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, flight_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Flight Number</Label>
                <Input
                  value={editFlightData.flight_number || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, flight_number: e.target.value.toUpperCase()})}
                />
              </div>
              <div>
                <Label>Registration</Label>
                <Input
                  value={editFlightData.registration || ""}
                  onChange={(e) => handleEditFlightRegistrationChange(e.target.value)}
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={editFlightData.time || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, time: e.target.value})}
                />
              </div>
              <div>
                <Label>Origin (auto-fill)</Label>
                <Input
                  value={editFlightData.origin || ""}
                  onChange={(e) => handleEditFlightOriginChange(e.target.value)}
                  placeholder="e.g., EBCI"
                />
                {editFlightData.origin_name && (
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin size={12} className="text-[#64748b]" />
                    <span className="text-xs text-[#64748b]">{editFlightData.origin_name}</span>
                    {editFlightData.origin_schengen === false && (
                      <Badge className="text-xs py-0 bg-red-100 text-red-700">⚠ NON-SCHENGEN</Badge>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label>Destination (auto-fill)</Label>
                <Input
                  value={editFlightData.destination || ""}
                  onChange={(e) => handleEditFlightDestinationChange(e.target.value)}
                  placeholder="e.g., LFPG"
                />
                {editFlightData.destination_name && (
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin size={12} className="text-[#64748b]" />
                    <span className="text-xs text-[#64748b]">{editFlightData.destination_name}</span>
                    {editFlightData.destination_schengen === false && (
                      <Badge className="text-xs py-0 bg-red-100 text-red-700">⚠ NON-SCHENGEN</Badge>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label>Aircraft Type</Label>
                <Input
                  value={editFlightData.aircraft_type || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, aircraft_type: e.target.value})}
                />
              </div>
              <div>
                <Label>Operator</Label>
                <Input
                  value={editFlightData.operator || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, operator: e.target.value})}
                />
              </div>
              <div>
                <Label>Type of Flight</Label>
                <Select
                  value={editFlightData.flight_type || "Passenger"}
                  onValueChange={(value) => setEditFlightData({...editFlightData, flight_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Passenger">Passenger</SelectItem>
                    <SelectItem value="Cargo">Cargo</SelectItem>
                    <SelectItem value="Private">Private</SelectItem>
                    <SelectItem value="Charter">Charter</SelectItem>
                    <SelectItem value="Military">Military</SelectItem>
                    <SelectItem value="State">State</SelectItem>
                    <SelectItem value="Diplomatic">Diplomatic</SelectItem>
                    <SelectItem value="Medical">Medical</SelectItem>
                    <SelectItem value="Government">Government</SelectItem>
                    <SelectItem value="Training">Training</SelectItem>
                    <SelectItem value="Positioning">Positioning</SelectItem>
                    <SelectItem value="Technical">Technical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>AOC</Label>
                <Input
                  value={editFlightData.aoc || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, aoc: e.target.value})}
                  placeholder="Air Operator Certificate"
                />
              </div>
              <div>
                <Label>Pilot in Command</Label>
                <Input
                  value={editFlightData.pilot_in_command || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, pilot_in_command: e.target.value})}
                />
              </div>
              <div>
                <Label>Crew Number</Label>
                <Input
                  type="number"
                  value={editFlightData.crew_number || 0}
                  onChange={(e) => setEditFlightData({...editFlightData, crew_number: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>PAX In</Label>
                <Input
                  type="number"
                  value={editFlightData.pax_in || 0}
                  onChange={(e) => setEditFlightData({...editFlightData, pax_in: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>PAX Out</Label>
                <Input
                  type="number"
                  value={editFlightData.pax_out || 0}
                  onChange={(e) => setEditFlightData({...editFlightData, pax_out: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Parking Position</Label>
                <Input
                  value={editFlightData.parking_position || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, parking_position: e.target.value.toUpperCase()})}
                />
              </div>
              <div>
                <Label>Actual Time (ATA/ATD)</Label>
                <Input
                  type="time"
                  value={editFlightData.atd || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, atd: e.target.value})}
                />
              </div>
              <div>
                <Label>MTOW (kg)</Label>
                <Input
                  type="number"
                  value={editFlightData.mtow || 0}
                  onChange={(e) => setEditFlightData({...editFlightData, mtow: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="col-span-3">
                <Label>Notes</Label>
                <Textarea
                  value={editFlightData.notes || ""}
                  onChange={(e) => setEditFlightData({...editFlightData, notes: e.target.value})}
                  rows={3}
                />
              </div>
              {/* Dynamic Services */}
              <div className="col-span-3 flex flex-wrap gap-4">
                {serviceOptions.map((service) => {
                  const isWeapon = service.id === "weapon_on_board";
                  const isWchr = service.id === "wchr_request";
                  const icon = serviceIconMap[service.icon] || "•";
                  
                  return (
                    <div key={service.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={editFlightData[service.id] || false}
                        onCheckedChange={(checked) => setEditFlightData({...editFlightData, [service.id]: checked})}
                      />
                      <Label className={`${
                        isWeapon ? "text-red-600 font-bold" : 
                        isWchr ? "text-blue-600 font-bold" : 
                        ""
                      }`}>
                        {icon} {service.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setEditFlightDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveEditedFlight}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#b8942c] text-white"
            >
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add Inbound/Outbound Flight Dialog */}
      <Dialog open={addFlightDialogOpen} onOpenChange={setAddFlightDialogOpen}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              {addFlightType === "inbound" ? (
                <><PlaneLanding size={24} className="text-green-600" /> Add Inbound Flight</>
              ) : (
                <><PlaneTakeoff size={24} className="text-blue-600" /> Add Outbound Flight</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2">
            <p className="text-sm text-yellow-800">
              <strong>Prefilled:</strong> Registration: {addFlightPrefill.registration || "N/A"}, 
              Operator: {addFlightPrefill.operator || "N/A"}, 
              Aircraft: {addFlightPrefill.aircraftType || "N/A"}
            </p>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <Label>Flight Date *</Label>
              <Input
                type="date"
                value={newFlightData.flight_date || ""}
                onChange={(e) => setNewFlightData({...newFlightData, flight_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Flight Number</Label>
              <Input
                value={newFlightData.flight_number || ""}
                onChange={(e) => setNewFlightData({...newFlightData, flight_number: e.target.value.toUpperCase()})}
                placeholder="e.g., ABC123"
              />
            </div>
            <div>
              <Label>Registration * (auto-fill)</Label>
              <Input
                value={newFlightData.registration || ""}
                onChange={(e) => handleNewFlightRegistrationChange(e.target.value)}
                className="bg-green-50"
                placeholder="e.g., OO-ABC"
              />
            </div>
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={newFlightData.time || ""}
                onChange={(e) => setNewFlightData({...newFlightData, time: e.target.value})}
              />
            </div>
            <div>
              <Label>Origin {addFlightType === "inbound" ? "(auto-fill)" : "(EBCI)"}</Label>
              <Input
                value={newFlightData.origin || ""}
                onChange={(e) => addFlightType === "inbound" ? handleNewFlightOriginChange(e.target.value) : null}
                className={addFlightType === "outbound" ? "bg-gray-100" : ""}
                readOnly={addFlightType === "outbound"}
                placeholder={addFlightType === "inbound" ? "e.g., LFPG" : ""}
              />
              {addFlightType === "inbound" && newFlightData.origin_name && (
                <div className="flex items-center gap-2 mt-1">
                  <MapPin size={12} className="text-[#64748b]" />
                  <span className="text-xs text-[#64748b]">{newFlightData.origin_name}</span>
                  {newFlightData.origin_schengen === false && (
                    <Badge className="text-xs py-0 bg-red-100 text-red-700">⚠ NON-SCHENGEN</Badge>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label>Destination {addFlightType === "outbound" ? "(auto-fill)" : "(EBCI)"}</Label>
              <Input
                value={newFlightData.destination || ""}
                onChange={(e) => addFlightType === "outbound" ? handleNewFlightDestinationChange(e.target.value) : null}
                className={addFlightType === "inbound" ? "bg-gray-100" : ""}
                readOnly={addFlightType === "inbound"}
                placeholder={addFlightType === "outbound" ? "e.g., LFPG" : ""}
              />
              {addFlightType === "outbound" && newFlightData.destination_name && (
                <div className="flex items-center gap-2 mt-1">
                  <MapPin size={12} className="text-[#64748b]" />
                  <span className="text-xs text-[#64748b]">{newFlightData.destination_name}</span>
                  {newFlightData.destination_schengen === false && (
                    <Badge className="text-xs py-0 bg-red-100 text-red-700">⚠ NON-SCHENGEN</Badge>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label>Aircraft Type</Label>
              <Input
                value={newFlightData.aircraft_type || ""}
                onChange={(e) => setNewFlightData({...newFlightData, aircraft_type: e.target.value})}
                className="bg-green-50"
              />
            </div>
            <div>
              <Label>Operator</Label>
              <Input
                value={newFlightData.operator || ""}
                onChange={(e) => setNewFlightData({...newFlightData, operator: e.target.value})}
                className="bg-green-50"
              />
            </div>
            <div>
              <Label>Pilot in Command</Label>
              <Input
                value={newFlightData.pilot_in_command || ""}
                onChange={(e) => setNewFlightData({...newFlightData, pilot_in_command: e.target.value})}
              />
            </div>
            <div>
              <Label>Crew Number</Label>
              <Input
                type="number"
                value={newFlightData.crew_number || 0}
                onChange={(e) => setNewFlightData({...newFlightData, crew_number: parseInt(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>PAX In</Label>
              <Input
                type="number"
                value={newFlightData.pax_in || 0}
                onChange={(e) => setNewFlightData({...newFlightData, pax_in: parseInt(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>PAX Out</Label>
              <Input
                type="number"
                value={newFlightData.pax_out || 0}
                onChange={(e) => setNewFlightData({...newFlightData, pax_out: parseInt(e.target.value) || 0})}
              />
            </div>
            <div className="col-span-3">
              <Label>Notes</Label>
              <Textarea
                value={newFlightData.notes || ""}
                onChange={(e) => setNewFlightData({...newFlightData, notes: e.target.value})}
                rows={2}
              />
            </div>
            {/* Dynamic Services */}
            <div className="col-span-3 flex flex-wrap gap-4">
              {serviceOptions.map((service) => {
                const isWeapon = service.id === "weapon_on_board";
                const isWchr = service.id === "wchr_request";
                const icon = serviceIconMap[service.icon] || "•";
                
                return (
                  <div key={service.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={newFlightData[service.id] || false}
                      onCheckedChange={(checked) => setNewFlightData({...newFlightData, [service.id]: checked})}
                    />
                    <Label className={`${
                      isWeapon ? "text-red-600 font-bold" : 
                      isWchr ? "text-blue-600 font-bold" : 
                      ""
                    }`}>
                      {icon} {service.label}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setAddFlightDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveNewFlight}
              disabled={saving}
              className={addFlightType === "inbound" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}
            >
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
              Create {addFlightType === "inbound" ? "Inbound" : "Outbound"} Flight
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aircraft Change Dialog - Update both movements in rotation */}
      <Dialog open={aircraftChangeDialogOpen} onOpenChange={setAircraftChangeDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <ArrowUpDown size={24} className="text-[#d4af37]" />
              Aircraft Change
            </DialogTitle>
            {/* Show rotation route summary */}
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-[#64748b]">Rotation:</span>
              <Badge className="bg-[#0f172a] text-white font-mono text-sm">
                {aircraftChangeData.arrival_origin || "---"} → EBCI → {aircraftChangeData.departure_destination || "---"}
              </Badge>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Registration (Common) */}
            <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]">
              <h3 className="font-semibold text-[#0f172a] mb-3 flex items-center gap-2">
                <Plane size={16} className="text-[#d4af37]" />
                Aircraft Registration
              </h3>
              <div>
                <Label className="text-[#64748b] text-sm">Registration (applies to all movements)</Label>
                <Input
                  value={aircraftChangeData.registration}
                  onChange={(e) => handleAircraftChangeRegistration(e.target.value)}
                  placeholder="e.g., EI-ABC"
                  className="font-mono max-w-[200px]"
                  data-testid="aircraft-change-registration"
                />
              </div>
            </div>
            
            {/* Arrival (Inbound) Details */}
            {rotationFlights.arrival && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-300">
                <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <PlaneLanding size={16} />
                  Inbound Flight
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[#64748b] text-sm">Flight Number</Label>
                    <Input
                      value={aircraftChangeData.arrival_flight_number}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_flight_number: e.target.value.toUpperCase()})}
                      placeholder="e.g., FR3533"
                      className="font-mono"
                      data-testid="aircraft-change-arrival-flight-number"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] text-sm">Origin</Label>
                    <Input
                      value={aircraftChangeData.arrival_origin}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_origin: e.target.value.toUpperCase()})}
                      placeholder="e.g., LFPB"
                      className="font-mono"
                      data-testid="aircraft-change-arrival-origin"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] text-sm">Destination</Label>
                    <Input
                      value={aircraftChangeData.arrival_destination}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_destination: e.target.value.toUpperCase()})}
                      placeholder="EBCI"
                      className="font-mono"
                      data-testid="aircraft-change-arrival-destination"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] text-sm">Date</Label>
                    <Input
                      type="date"
                      value={aircraftChangeData.arrival_date}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_date: e.target.value})}
                      data-testid="aircraft-change-arrival-date"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] text-sm">Time (STA)</Label>
                    <Input
                      type="time"
                      value={aircraftChangeData.arrival_time}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_time: e.target.value})}
                      data-testid="aircraft-change-arrival-time"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[#64748b] text-sm">PAX ↓</Label>
                      <Input
                        type="number"
                        min="0"
                        value={aircraftChangeData.arrival_pax_in}
                        onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_pax_in: e.target.value})}
                        data-testid="aircraft-change-arrival-pax-in"
                      />
                    </div>
                    <div>
                      <Label className="text-[#64748b] text-sm">PAX ↑</Label>
                      <Input
                        type="number"
                        min="0"
                        value={aircraftChangeData.arrival_pax_out}
                        onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_pax_out: e.target.value})}
                        data-testid="aircraft-change-arrival-pax-out"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Departure (Outbound) Details */}
            {rotationFlights.departure && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-300">
                <h3 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <PlaneTakeoff size={16} />
                  Outbound Flight
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[#64748b] text-sm">Flight Number</Label>
                    <Input
                      value={aircraftChangeData.departure_flight_number}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_flight_number: e.target.value.toUpperCase()})}
                      placeholder="e.g., FR3534"
                      className="font-mono"
                      data-testid="aircraft-change-departure-flight-number"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] text-sm">Origin</Label>
                    <Input
                      value={aircraftChangeData.departure_origin}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_origin: e.target.value.toUpperCase()})}
                      placeholder="EBCI"
                      className="font-mono"
                      data-testid="aircraft-change-departure-origin"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] text-sm">Destination</Label>
                    <Input
                      value={aircraftChangeData.departure_destination}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_destination: e.target.value.toUpperCase()})}
                      placeholder="e.g., GMMX"
                      className="font-mono"
                      data-testid="aircraft-change-departure-destination"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] text-sm">Date</Label>
                    <Input
                      type="date"
                      value={aircraftChangeData.departure_date}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_date: e.target.value})}
                      data-testid="aircraft-change-departure-date"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] text-sm">Time (STD)</Label>
                    <Input
                      type="time"
                      value={aircraftChangeData.departure_time}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_time: e.target.value})}
                      data-testid="aircraft-change-departure-time"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[#64748b] text-sm">PAX ↓</Label>
                      <Input
                        type="number"
                        min="0"
                        value={aircraftChangeData.departure_pax_in}
                        onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_pax_in: e.target.value})}
                        data-testid="aircraft-change-departure-pax-in"
                      />
                    </div>
                    <div>
                      <Label className="text-[#64748b] text-sm">PAX ↑</Label>
                      <Input
                        type="number"
                        min="0"
                        value={aircraftChangeData.departure_pax_out}
                        onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_pax_out: e.target.value})}
                        data-testid="aircraft-change-departure-pax-out"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setAircraftChangeDialogOpen(false)}
              className="border-[#64748b] text-[#64748b]"
            >
              Cancel
            </Button>
            <Button
              onClick={saveAircraftChange}
              disabled={saving || !aircraftChangeData.registration}
              className="bg-[#d4af37] hover:bg-[#b8972f] text-white"
              data-testid="save-aircraft-change-btn"
            >
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Apply Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
